import type { StorySettings } from "../types";

/**
 * Голоса рассказчика — готовые характеры вместо пяти чисел. Диалы никуда не
 * делись, но перестали быть обязательным этапом: новичок выбирает характер,
 * а пульт открывает тот, кому действительно нужно докрутить.
 *
 * Пороги в `voiceSummary` совпадают с `server/story-controls.ts` (≤33 / ≥67) —
 * если правится там, правится и здесь, иначе студия начнёт обещать одно,
 * а редакция получать другое.
 */
export type VoiceDials = Pick<StorySettings, "mode" | "humor" | "factuality" | "tempo" | "drama" | "imagery">;

export interface VoicePreset extends VoiceDials {
  id: string;
  label: string;
  blurb: string;
  icon: string;
}

export const VOICE_EXAMPLES: Record<string, string> = {
  fireside: "Попробуйте договориться о встрече без часов. «После обеда» звучит понятно — пока не выяснится, что обедаете вы в разное время.",
  investigation: "Двое пришли вовремя, но так и не встретились. Прежде чем искать виноватого, стоит выяснить: кто и как определял время?",
  skeptic: "«Вовремя» — не универсальная величина. Без общей точки отсчёта это слово сообщает скорее о привычках говорящего, чем о часе встречи.",
  naturalist: "Удивительно, сколько способов заметить время вокруг: движется тень, остывает чай, меняется свет. Но как превратить наблюдение в общую мерку?",
  tavern: "У встречи «после обеда» один недостаток: чей именно обед считать главным? Кажется, часам нашлась работа.",
  expedition: "Вы выходите навстречу, сверяясь с солнцем. На другой стороне города человек делает то же самое. Между вами — расстояние и два разных «сейчас».",
  chronicle: "Встреча сорвалась. Каждый уверен, что пришёл вовремя. Когда от таких договорённостей зависит больше, чем один разговор, нужна общая мера.",
  cinema: "Тень медленно пересекает порог. На столе стынет чай. Человек у окна смотрит на улицу: гость должен был прийти после обеда.",
};

export const VOICE_PRESETS: VoicePreset[] = [
  {
    id: "fireside", label: "Хороший собеседник", icon: "flame",
    blurb: "Спокойно, ясно и с вниманием к человеческим деталям.",
    mode: "fireside", humor: 45, factuality: 55, tempo: 35, drama: 30, imagery: 60,
  },
  {
    id: "investigation", label: "По следам", icon: "search",
    blurb: "Версии, улики и разбор чужих заблуждений.",
    mode: "detective", humor: 30, factuality: 80, tempo: 60, drama: 70, imagery: 50,
  },
  {
    id: "skeptic", label: "Скептик с картотекой", icon: "scale",
    blurb: "Сухо и строго: спорное названо спорным.",
    mode: "fireside", humor: 35, factuality: 95, tempo: 50, drama: 25, imagery: 30,
  },
  {
    id: "naturalist", label: "Восторженный ботаник", icon: "microscope",
    blurb: "Объясняет, как всё устроено, и сам этому радуется.",
    mode: "fireside", humor: 65, factuality: 75, tempo: 55, drama: 35, imagery: 70,
  },
  {
    id: "tavern", label: "Травим байки", icon: "sparkles",
    blurb: "Весело и на грани байки, но легенда помечена легендой.",
    mode: "fireside", humor: 90, factuality: 40, tempo: 70, drama: 45, imagery: 55,
  },
  {
    id: "expedition", label: "Экспедиция", icon: "compass",
    blurb: "Движение, сцены и ощущение пути.",
    mode: "adventure", humor: 40, factuality: 70, tempo: 75, drama: 65, imagery: 75,
  },
  {
    id: "chronicle", label: "Хроника катастрофы", icon: "storm",
    blurb: "Ставки, решения и последствия — без преувеличений.",
    mode: "adventure", humor: 15, factuality: 85, tempo: 65, drama: 90, imagery: 60,
  },
  {
    id: "cinema", label: "Кинолента", icon: "film",
    blurb: "Свет, звук и жесты: история собирается как фильм.",
    mode: "adventure", humor: 45, factuality: 60, tempo: 50, drama: 60, imagery: 95,
  },
];

/**
 * С чего начинает новый читатель: вечерняя история голосом «Хороший собеседник» и сверка
 * с источниками. Набор обязан совпадать с одним из голосов — иначе студия
 * встретит новичка непонятным «своим набором» вместо выбранного характера.
 */
export const DEFAULT_STORY_SETTINGS: StorySettings = {
  length: "evening",
  webResearch: true,
  illustrations: false,
  mode: VOICE_PRESETS[0].mode,
  humor: VOICE_PRESETS[0].humor,
  factuality: VOICE_PRESETS[0].factuality,
  tempo: VOICE_PRESETS[0].tempo,
  drama: VOICE_PRESETS[0].drama,
  imagery: VOICE_PRESETS[0].imagery,
};

export const MODE_LABELS: Record<StorySettings["mode"], string> = {
  fireside: "Разговор",
  adventure: "Приключение",
  detective: "Детектив",
};

/** Ровно тот же пресет, что выбран, или ничего — тогда у читателя «свой набор». */
export function matchVoicePreset(settings: VoiceDials) {
  return VOICE_PRESETS.find((preset) => preset.mode === settings.mode
    && preset.humor === settings.humor && preset.factuality === settings.factuality
    && preset.tempo === settings.tempo && preset.drama === settings.drama && preset.imagery === settings.imagery);
}

export function applyVoicePreset(settings: StorySettings, preset: VoicePreset): StorySettings {
  return {
    ...settings,
    mode: preset.mode, humor: preset.humor, factuality: preset.factuality,
    tempo: preset.tempo, drama: preset.drama, imagery: preset.imagery,
  };
}

function range(value: number, low: string, middle: string, high: string) {
  if (value <= 33) return low;
  if (value >= 67) return high;
  return middle;
}

/** Короткое ТЗ человеческим языком: что именно уедет в редакцию с этими настройками. */
export function voiceSummary(settings: VoiceDials) {
  return [
    MODE_LABELS[settings.mode],
    range(settings.humor, "почти без шуток", "шутки к месту", "острая ирония"),
    range(settings.factuality, "факты и версии", "осторожно со спорным", "строгая подача"),
    range(settings.tempo, "неторопливо", "ровный ритм", "ближе к действию"),
    range(settings.drama, "без нагнетания", "ощутимые ставки", "конфликт и повороты"),
    range(settings.imagery, "точные детали", "выразительно и ясно", "кинематографично"),
  ];
}
